/* Green Gorilla Fruits — predictive search
 * --------------------------------------------------------------
 * Wires the header search input to Shopify's Predictive Search API
 * (`/search/suggest.json`). Behaviour:
 *
 *  - Triggers a fetch when the query is >= 2 chars after a 250ms
 *    debounce.
 *  - Cancels in-flight requests via AbortController so only the
 *    latest query renders.
 *  - Renders up to 4 products + 4 articles + 2 pages.
 *  - Shows "no results" copy when the query returns an empty set.
 *  - Falls back to a normal form submit (Enter key) which navigates
 *    to /search?q=, so the experience still works without JS.
 */
(function () {
  'use strict';

  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 250;
  const PRODUCT_LIMIT = 4;
  const ARTICLE_LIMIT = 4;
  const PAGE_LIMIT = 2;

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getResultsUrl(query) {
    // Shopify's `resources[limit]` is a single shared cap across all
    // resource types — there is no per-type limit on the API. We
    // request the highest of our per-type caps and slice each list to
    // its own per-type limit (`PAGE_LIMIT`, etc.) on the client.
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,article,page',
      'resources[limit]': String(Math.max(PRODUCT_LIMIT, ARTICLE_LIMIT, PAGE_LIMIT)),
      'resources[options][unavailable_products]': 'last',
    });
    return `/search/suggest.json?${params.toString()}`;
  }

  function renderProduct(item) {
    const image = item.featured_image
      ? `<img src="${escapeHtml(item.featured_image.url)}" alt="${escapeHtml(item.title)}" loading="lazy" width="64" height="80" class="w-16 aspect-[4/5] object-cover shrink-0">`
      : '';
    const price = item.price
      ? `<span class="font-label text-xs text-gold">${escapeHtml(item.price)}</span>`
      : '';
    return `
      <li>
        <a href="${escapeHtml(item.url)}" class="flex items-center gap-4 px-4 py-3 hover:bg-gold/10 transition-colors">
          ${image}
          <span class="min-w-0 flex-1">
            <span class="block font-body text-sm text-surface truncate">${escapeHtml(item.title)}</span>
            ${price}
          </span>
        </a>
      </li>
    `;
  }

  function renderArticle(item) {
    // Decode item.summary_html (already plain text after stripping HTML
    // tags) once via DOMParser so that entities like `&amp;` are not
    // double-escaped when we later run the string through escapeHtml().
    const rawSummary = item.summary
      ? item.summary
      : item.summary_html
        ? new DOMParser().parseFromString(item.summary_html, 'text/html').body.textContent || ''
        : '';
    const summary = rawSummary.slice(0, 80);
    return `
      <li>
        <a href="${escapeHtml(item.url)}" class="flex flex-col gap-1 px-4 py-3 hover:bg-gold/10 transition-colors">
          <span class="font-body text-sm text-surface">${escapeHtml(item.title)}</span>
          ${summary ? `<span class="font-label text-[0.65rem] tracking-widest uppercase text-surface/60">${escapeHtml(summary)}</span>` : ''}
        </a>
      </li>
    `;
  }

  function renderPage(item) {
    return `
      <li>
        <a href="${escapeHtml(item.url)}" class="flex flex-col gap-1 px-4 py-3 hover:bg-gold/10 transition-colors">
          <span class="font-body text-sm text-surface">${escapeHtml(item.title)}</span>
        </a>
      </li>
    `;
  }

  function renderGroup(title, items, renderer) {
    if (!items || items.length === 0) return '';
    return `
      <div class="border-t border-gold/10 first:border-t-0">
        <h3 class="px-4 pt-3 pb-1 font-label text-[0.65rem] tracking-[0.3em] uppercase text-gold">${escapeHtml(title)}</h3>
        <ul class="divide-y divide-gold/5">${items.map(renderer).join('')}</ul>
      </div>
    `;
  }

  function renderResults(panel, data, query, copy) {
    const products =
      (data.resources && data.resources.results && data.resources.results.products) || [];
    const articles =
      (data.resources && data.resources.results && data.resources.results.articles) || [];
    const pages = (data.resources && data.resources.results && data.resources.results.pages) || [];

    const productsCapped = products.slice(0, PRODUCT_LIMIT);
    const articlesCapped = articles.slice(0, ARTICLE_LIMIT);
    const pagesCapped = pages.slice(0, PAGE_LIMIT);

    const total = productsCapped.length + articlesCapped.length + pagesCapped.length;

    if (total === 0) {
      panel.innerHTML = `
        <p class="px-4 py-6 text-center font-body text-sm text-surface/60 italic">${escapeHtml(copy.noResults)}</p>
      `;
      return;
    }

    const groups = [
      renderGroup(copy.products, productsCapped, renderProduct),
      renderGroup(copy.articles, articlesCapped, renderArticle),
      renderGroup(copy.pages, pagesCapped, renderPage),
    ].join('');

    const viewAllUrl = `/search?q=${encodeURIComponent(query)}`;
    const viewAll = `
      <div class="border-t border-gold/20 px-4 py-3">
        <a href="${escapeHtml(viewAllUrl)}" class="block text-center font-label text-[0.7rem] tracking-[0.3em] uppercase text-gold hover:text-gold-bright">
          ${escapeHtml(copy.viewAll)}
        </a>
      </div>
    `;

    panel.innerHTML = groups + viewAll;
  }

  function bindPredictiveSearch(drawer) {
    const input = drawer.querySelector('input[type="search"]');
    const panel = drawer.querySelector('[data-gg-search-results]');
    if (!input || !panel) return;

    const copy = {
      products: panel.dataset.copyProducts || 'Products',
      articles: panel.dataset.copyArticles || 'Articles',
      pages: panel.dataset.copyPages || 'Pages',
      noResults: panel.dataset.copyNoResults || 'No results found.',
      viewAll: panel.dataset.copyViewAll || 'View all results',
    };

    let timer = null;
    let controller = null;

    function reset() {
      if (controller) controller.abort();
      controller = null;
      panel.innerHTML = '';
      panel.hidden = true;
    }

    function runQuery(query) {
      if (controller) controller.abort();
      controller = new AbortController();
      panel.hidden = false;

      fetch(getResultsUrl(query), {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
        .then((data) => renderResults(panel, data, query, copy))
        .catch((err) => {
          if (err.name === 'AbortError') return;
          panel.innerHTML = `<p class="px-4 py-6 text-center font-body text-sm text-surface/60 italic">${escapeHtml(copy.noResults)}</p>`;
        });
    }

    input.addEventListener('input', () => {
      const query = input.value.trim();
      window.clearTimeout(timer);
      if (query.length < MIN_CHARS) {
        reset();
        return;
      }
      timer = window.setTimeout(() => runQuery(query), DEBOUNCE_MS);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') reset();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-gg-search-drawer]').forEach(bindPredictiveSearch);
  });
})();
